import * as React from "react";
import { ThemeProvider } from "@emotion/react";
import { ErrorBoundary } from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Alert, App, ConfigProvider } from "antd5";
import classNames from "classnames";
import { lightTheme } from "styles/theme";

import { CurrentUserConfiguredProviders } from "components/app_layout/CurrentUserConfiguredProviders";
import { DefaultTheme } from "components/app_layout/DefaultTheme";
import { IntegrationAPIProvider } from "lib/integrationApiContext";
import { OpenAPIProvider } from "lib/openApiContext";
import { DialogManagerProvider } from "lib/providers/DialogManager";
import { RecordViewerProvider } from "lib/providers/RecordViewer";
import { AdminAPIProvider } from "lib/stotlesAdminApiContext";
import StotlesAPI from "lib/StotlesApi";
import { ApiContextProvider } from "lib/stotlesApiContext";
import { ErrorBoundaryFallback } from "../../components/app_layout/ErrorBoundaryFallback";
import Toaster from "../../components/app_layout/Toaster";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { AppTokenProvider } from "../../lib/providers/ApiTokenProvider";
import { PaywallProvider } from "../../lib/providers/Paywall";
import { ProHelperProvider } from "../../lib/providers/ProHelper";

import css from "./AdminComponent.module.scss";

const REACT_QUERY_CLIENT = new QueryClient();
const STOTLES_API = new StotlesAPI();
type AdminErrorBoundaryState = {
  error: Error | undefined;
};

type AdminErrorBoundaryProps = {
  children: React.ReactNode;
};
class AdminErrorBoundary extends React.Component<AdminErrorBoundaryProps, AdminErrorBoundaryState> {
  constructor(props: AdminErrorBoundaryProps) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  // We don't override `componentDidCatch` so that errors bubble up to window.onerror
  // and can be caught by sentry

  render() {
    if (this.state.error) {
      return (
        <Alert
          type="error"
          message="Error occurred"
          showIcon
          description={
            <div>
              <p>{this.state.error.message}</p>
              <pre>{this.state.error.stack}</pre>
            </div>
          }
        />
      );
    }
    return this.props.children;
  }
}

type AdminComponentOptions = {
  fullHeight?: boolean;
};

export function WrapAdminComponent<Props extends object>(
  Component: React.ComponentType<Props>,
  options?: AdminComponentOptions,
) {
  // eslint-disable-next-line react/display-name
  return (props: Props): JSX.Element => (
    <ConfigProvider prefixCls="ant5" theme={DefaultTheme}>
      <App className={classNames({ [css.fullHeightAdminApp]: !!options?.fullHeight })}>
        <ErrorBoundary showDialog fallback={(props) => <ErrorBoundaryFallback {...props} />}>
          <React.Suspense fallback={<CentredSpinner />}>
            <QueryClientProvider client={REACT_QUERY_CLIENT}>
              <ApiContextProvider api={STOTLES_API}>
                <OpenAPIProvider>
                  <AppTokenProvider>
                    <CurrentUserConfiguredProviders>
                      <ThemeProvider theme={lightTheme}>
                        <AdminAPIProvider>
                          <IntegrationAPIProvider>
                            <AdminErrorBoundary>
                              <DialogManagerProvider>
                                <PaywallProvider>
                                  <ProHelperProvider>
                                    <RecordViewerProvider>
                                      <Toaster />
                                      <Component {...props} />
                                    </RecordViewerProvider>
                                  </ProHelperProvider>
                                </PaywallProvider>
                              </DialogManagerProvider>
                            </AdminErrorBoundary>
                          </IntegrationAPIProvider>
                        </AdminAPIProvider>
                      </ThemeProvider>
                    </CurrentUserConfiguredProviders>
                  </AppTokenProvider>
                </OpenAPIProvider>
              </ApiContextProvider>
            </QueryClientProvider>
          </React.Suspense>
        </ErrorBoundary>
      </App>
    </ConfigProvider>
  );
}
